/**
 * Call saga index.
 * Defines which actions trigger which sagas.
 */

// Call plugin.
import * as actionTypes from '../../interfaceNew/actionTypes'
import * as webrtcActionTypes from '../../../webrtc/interface/actionTypes'
import * as connectivityActionTypes from '../../../connectivity/interface/actionTypes'
import * as supportSagas from './support'
import * as requests from '../requests/calls'
import { getRemoteParticipant } from './utils'
import { CALL_STATES, CALL_MEDIA_CONNECTION_STATES } from '../../constants'
import { getCallByWrtcsSessionId, getOptions } from '../../interfaceNew/selectors'

// Callstack plugin.
import {
  makeCall,
  sendRingingFeedback,
  answerCall,
  rejectCall,
  ignoreCall,
  forwardCall
} from '../../../callstack/call/establish'
import * as midcallSagas from '../../../callstack/call/midcall'
import {
  incomingCall,
  parseCallRequest,
  parseCallResponse,
  callStatusUpdateEnded,
  sessionStatusUpdateEnded,
  callStatusUpdateRinging,
  callStatusUpdateFailed,
  callCancelled,
  receiveEarlyMedia
} from '../../../callstack/call/notifications'
import { sendDtmf } from '../../../callstack/call/dtmf'
import {
  sendCallAudit,
  getSessions,
  resyncCallState,
  normalizeIceFailure,
  callIceCollectionCheck
} from '../../../callstack/call/support'
import { getAvailableCodecs } from '../../../callstack/webrtc/codecs'

// Libraries.
import { call, select, takeEvery, takeLeading } from 'redux-saga/effects'

// Other plugins
import { NOTIFICATION_RECEIVED } from '../../../notifications/interface/actionTypes'
import { SUBSCRIBE_FINISHED } from '../../../subscription/interface/actionTypes'
import { CONNECT_FINISHED } from '../../../auth/interface/actionTypes'
import { logManager } from '../../../logs'

const log = logManager.getLogger('CALL')

/**
 * Call operations.
 */

/**
 * Start an outgoing call.
 * @method createCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * createCall (deps) {
  yield takeEvery(actionTypes.MAKE_CALL, makeCall, { ...deps, requests })
}

/**
 * Sending the "ringing feedback" notification to the server.
 * @method sendRingingEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * sendRingingEntry (deps) {
  yield takeEvery(actionTypes.SEND_RINGING_FEEDBACK, sendRingingFeedback, { ...deps, requests })
}

/**
 * Answer a ringing call.
 * @method answerCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * answerCallEntry (deps) {
  yield takeEvery(actionTypes.ANSWER_CALL, answerCall, { ...deps, requests })
}

/**
 * Reject a ringing call.
 * @method rejectCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * rejectCallEntry (deps) {
  yield takeEvery(actionTypes.REJECT_CALL, rejectCall, { ...deps, requests })
}

/**
 * Add media to a call.
 * @method addMedia
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * addMediaEntry (deps) {
  yield takeEvery(actionTypes.ADD_MEDIA, midcallSagas.addMedia, { ...deps, requests })
}

/**
 * Remove media from a call.
 * @method removeMedia
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * removeMediaEntry (deps) {
  yield takeEvery(actionTypes.REMOVE_MEDIA, midcallSagas.removeMedia, { ...deps, requests })
}

/**
 * Add media to a call.
 * @method addMedia
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * addBasicMediaEntry (deps) {
  yield takeEvery(actionTypes.ADD_BASIC_MEDIA, midcallSagas.addBasicMedia, { ...deps, requests })
}

/**
 * Remove media from a call.
 * @method removeMedia
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * removeBasicMediaEntry (deps) {
  yield takeEvery(actionTypes.REMOVE_BASIC_MEDIA, midcallSagas.removeBasicMedia, { ...deps, requests })
}

/**
 * Send DTMF tones for a call.
 * @method sendDTMF
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * sendDtmfEntry (deps) {
  /**
   * Use `takeLeading` to avoid multiple operations happening at the same time.
   *    This is an issue for in-band DTMF, which would cause the second and all
   *    subsequent sendDtmf operations to fail if the first operation has not
   *    yet finished.
   */
  yield takeLeading(actionTypes.SEND_DTMF, sendDtmf, { ...deps })
}

/**
 * Call notifications.
 */

/**
 * Handle a Link "call incoming" notification.
 * @method incomingCallNotification
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * incomingCallNotification (deps) {
  /**
   * Intercept the Link-specific notification, ensuring parameters are given
   *    to the Callstack sagas into a generic format.
   * @method linkIncomingCall
   * @param {Object} action An "incoming call notification" action.
   */
  function * linkIncomingCall (action) {
    const message = action.payload.notificationMessage

    const notificationParams = message.callNotificationParams || {}

    // Massage data into a generic format, instead of Link-specific.
    const params = {
      // The remote SDP offer included with the notification (if any).
      sdp: message.sessionParams.sdp,
      // ID that the server uses to identify the session.
      wrtcsSessionId: message.sessionParams.sessionData,
      // Remote participant information.
      remoteName: notificationParams.callerName,
      remoteNumber: notificationParams.callerDisplayNumber,
      // Where the call was sent
      calleeNumber: notificationParams.calleeDisplayNumber,
      // Custom Parameters
      customParameters: message.customParameters
    }

    // Pass the incoming call parameters to the Callstack for handling.
    yield call(incomingCall, { ...deps, requests }, params, action.meta.channel)
  }

  // Redux-saga take() pattern.
  function incomingCallPattern (action) {
    return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === 'call'
  }
  yield takeEvery(incomingCallPattern, linkIncomingCall)
}

/**
 * Handle a Link "session progress" notification.
 * @method sessionProgressNotification
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * sessionProgressNotification (deps) {
  function progressPattern (action) {
    return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === 'sessionProgress'
  }

  /**
   * Intercept the notification before its sent to the Callstack. We do this
   *    to ensure the data is in a generic format.
   * @method parseSessionProgress
   */
  function * parseSessionProgress (action) {
    const message = action.payload.notificationMessage

    const notificationParams = message.callNotificationParams || {}

    // Massage data into a generic format, instead of Link-specific.
    const params = {
      sdp: message.sessionParams.sdp,
      wrtcsSessionId: message.sessionParams.sessionData,
      remoteName: notificationParams.remoteName,
      remoteNumber: notificationParams.remoteDisplayNumber,
      customParameters: message.customParameters
    }

    yield call(receiveEarlyMedia, deps, params)
  }

  yield takeEvery(progressPattern, parseSessionProgress)
}

/**
 * Handle a call status websocket notification.
 * @method callStatusNotification
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * callStatusNotification (deps) {
  function statusUpdatePattern (status) {
    return function statusPattern (action) {
      return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === status
    }
  }

  function * parseStatusNotification (action) {
    const message = action.payload.notificationMessage
    const { eventType } = message
    const { sessionData: wrtcsSessionId, reasonText, statusCode } = message.sessionParams

    const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)
    if (!targetCall) {
      log.info('A remote operation is being requested on a call that no longer exists.')
    }

    const remoteInfo = yield call(getRemoteParticipant, message)

    const params = {
      wrtcsSessionId,
      reasonText,
      statusCode,
      // Remote participant's information.
      ...remoteInfo
    }
    if (eventType === 'callEnd') {
      yield call(callStatusUpdateEnded, deps, params)
    } else if (eventType === 'sessionComplete') {
      yield call(sessionStatusUpdateEnded, { ...deps, requests }, params)
    } else if (eventType === 'ringing') {
      params.customParameters = message.customParameters
      yield call(callStatusUpdateRinging, deps, params)
    } else if (eventType === 'sessionFail') {
      yield call(callStatusUpdateFailed, deps, params)
    }
  }

  // Handle specific call statuses
  yield takeEvery(statusUpdatePattern('ringing'), parseStatusNotification)
  yield takeEvery(statusUpdatePattern('callEnd'), parseStatusNotification)
  yield takeEvery(statusUpdatePattern('sessionComplete'), parseStatusNotification)
  yield takeEvery(statusUpdatePattern('sessionFail'), parseStatusNotification)
}

/**
 * Handle a `callCancel` notification from Link.
 * @method callCancelNotification
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * callCancelNotification (deps) {
  // Redux-saga take pattern.
  function callCancelPattern (action) {
    return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === 'callCancel'
  }

  /**
   * Handle Link-specific behaviours before passing the notification off to the
   *    Callstack.
   * @method handleCallCancel
   * @param  {Object} action
   */
  function * handleCallCancel (action) {
    // Get the current call
    const { sessionData } = action.payload.notificationMessage.sessionParams
    const currentCall = yield select(getCallByWrtcsSessionId, sessionData)

    /*
     * The backend sends a "callCancel" notification immediately after answering
     *    (and rejecting, forwarding) a call. The backend's intention for this
     *    is to notify other user subscriptions (eg. same user on another
     *    device) that the call has been handled, so the call is "cancelled" for
     *    this subscription. But they send it as a broadcast notification to all
     *    subscriptions, even the one that handled the call.
     *
     * We need to ignore this notification for the subscription that handled the
     *    call, but process it for any other subscriptions. We do this in two
     *    ways:
     *      1) If the Call state indicates we are obviously the device that
     *        answered it (eg. the Call is connected), ignore it.
     *      2) If a flag is set on the Call that indicates we are in-progress
     *        to handle it, ignore it.
     *    This handles receiving the callCancel before and after the in-progress
     *        operation has finished for both regular and slow-start calls.
     */

    if (
      currentCall &&
      currentCall.state !== CALL_STATES.RINGING &&
      currentCall.state !== CALL_STATES.EARLY_MEDIA &&
      currentCall.state !== CALL_STATES.INITIATED
    ) {
      // Don't process the notification if the call isn't "ringing".
      log.debug(`Received call cancel notification when state is ${currentCall.state}. Ignoring.`)
      return
    }

    if (currentCall.isHandling) {
      // Don't process the notification if we are currently handling the call.
      log.debug('Received call cancel notification while handling is in-progress. Ignoring.')
      return
    }

    const params = {
      wrtcsSessionId: sessionData
    }

    // Now process the notification after making sure we don't want to ignore it.
    yield call(callCancelled, deps, params)
  }

  yield takeEvery(callCancelPattern, handleCallCancel)
}

/**
 * Handle receiving remote offer notifications in a Link format.
 * Uses properties in the notification to create a standardized data object to
 *    be used by the Callstack.
 * @method receiveRemoteOffer
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * receiveRemoteOffer (deps) {
  /**
   * Intercept the Link-specific notification, ensuring parameters are given
   *    to the Callstack sagas into a generic format.
   * @method parseOfferNotification
   * @param {Object} action
   */
  function * parseOfferNotification (action) {
    const message = action.payload.notificationMessage
    const { sessionData: wrtcsSessionId } = message.sessionParams

    const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)
    if (!targetCall) {
      log.info('A remote operation is being requested on a call that no longer exists.')
      return
    }

    const remoteInfo = yield call(getRemoteParticipant, message)

    // Pull-out the parameters into a standard format for the Callstack.
    const params = {
      wrtcsSessionId: message.sessionParams.sessionData,
      sdp: message.sessionParams.sdp,
      ...remoteInfo,
      customParameters: message.customParameters
    }

    // Pass the call parameters to the Callstack for handling.
    yield call(parseCallRequest, { ...deps, requests }, params)
  }

  // take() pattern for "update call w/ offer" notifications.
  function receiveOfferPattern (action) {
    return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === 'startCallUpdate'
  }

  yield takeEvery(receiveOfferPattern, parseOfferNotification)
}

/**
 * Handle receiving remote answer notifications in a Link format.
 * Uses properties in the notification to create a standardized data object to
 *    be used by the Callstack.
 * @method receiveRemoteAnswer
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * receiveRemoteAnswer (deps) {
  /**
   * Intercept the Link-specific notification, ensuring parameters are given
   *    to the Callstack sagas into a generic format.
   * @method parseAnswerNotification
   * @param {Object} action
   */
  function * parseAnswerNotification (action) {
    const message = action.payload.notificationMessage
    const { sessionData: wrtcsSessionId } = message.sessionParams

    const targetCall = yield select(getCallByWrtcsSessionId, wrtcsSessionId)
    if (!targetCall) {
      log.info('A remote operation is being requested on a call that no longer exists.')
      return
    }

    const remoteInfo = yield call(getRemoteParticipant, message)

    // Pull-out the parameters into a standard format for the Callstack.
    const params = {
      wrtcsSessionId: message.sessionParams.sessionData,
      sdp: message.sessionParams.sdp,

      retryAfter: message.sessionParams.retryAfter,
      message: message.sessionParams.reasonText,
      code: message.statusCode,

      // Remote participant information.
      ...remoteInfo,

      customParameters: message.customParameters
    }

    // Pass the call parameters to the Callstack for handling.
    yield call(parseCallResponse, { ...deps, requests }, params)
  }

  // take() pattern for "update call with answer" notifications.
  function receiveAnswerPattern (action) {
    return action.type === NOTIFICATION_RECEIVED && action.payload.notificationMessage.eventType === 'respondCallUpdate'
  }

  yield takeEvery(receiveAnswerPattern, parseAnswerNotification)
}

/**
 * End an ongoing call.
 * @method endCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * endCallEntry (deps) {
  yield takeEvery(actionTypes.END_CALL, midcallSagas.endCall, { ...deps, requests })
}

/**
 * Ignore an incoming call.
 * @method ignoreCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * ignoreCallEntry (deps) {
  yield takeEvery(actionTypes.IGNORE_CALL, ignoreCall, { ...deps })
}

/**
 * Put a call on hold.
 * Currently, "hold" is defined as setting media directions to inactive.
 * TODO: There will be different "hold" scenarios. Need to determine how to
 *    differentiate between them to know which saga to trigger.
 * @method holdCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * holdCall (deps) {
  yield takeEvery(actionTypes.CALL_HOLD, midcallSagas.offerInactiveMedia, { ...deps, requests })
}

/**
 * Take a call off hold.
 * Currently, "unhold" is defined as setting media directions to sendrecv.
 * TODO: There will be different "unhold" scenarios. Need to determine how to
 *    differentiate between them to know which saga to trigger.
 * @method unholdCall
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * unholdCall (deps) {
  yield takeEvery(actionTypes.CALL_UNHOLD, midcallSagas.offerFullMedia, { ...deps, requests })
}

/**
 * Sends custom parameters for a call
 * @method sendCustomParameters
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * sendCustomParameters (deps) {
  yield takeEvery(actionTypes.SEND_CUSTOM_PARAMETERS, midcallSagas.sendCustomParameters, { ...deps, requests })
}

/**
 * Handle sending a call audit request
 * TODO: Move the common "setup" logic into the Callstack?
 * @method callAudit
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * callAudit (deps) {
  const actionTypesToDoAuditOn = [actionTypes.UPDATE_CALL, actionTypes.PENDING_JOIN, actionTypes.PENDING_MAKE_CALL]

  function callStartAuditPattern (action) {
    if (!action.error) {
      if (actionTypesToDoAuditOn.indexOf(action.type) !== -1) {
        const preConnectedState =
          action.payload.state === CALL_STATES.INITIATED || action.payload.state === CALL_STATES.RINGING
        return preConnectedState
      }
    }
    return false
  }

  yield takeEvery(callStartAuditPattern, sendCallAudit, {
    webRTC: deps.webRTC,
    requests
  })
}

/**
 * Check (and update) the session status of all active calls upon websocket connection to ensure all call states are up to date.
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * getSessionsOnWSConnect (deps) {
  yield takeEvery(connectivityActionTypes.WS_CONNECT_FINISHED, getSessions, { ...deps, requests })
}

/**
 * Check (and update) the state of a call to ensure the call is synced with the server.
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * resyncEntry (deps) {
  yield takeEvery(actionTypes.RESYNC, resyncCallState, { ...deps, requests })
}

/**
 * Get RTCStatsReport.
 * @method getStats
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * getStatsEntry (deps) {
  yield takeEvery(actionTypes.GET_STATS, midcallSagas.getStats, deps)
}

/**
 * Handles updating TURN information after the user connects.
 * @method setTurnCredentials
 */
export function * setTurnCredentials () {
  // Check that the action is a successful subscription that includes
  //    TURN information from the server.
  function hasTurnCredentials (action) {
    return (
      !action.error &&
      ((action.type === CONNECT_FINISHED &&
        action.payload.subscription &&
        action.payload.subscription.turnCredentials) ||
        (action.type === SUBSCRIBE_FINISHED &&
          action.payload.subscriptions &&
          action.payload.subscriptions.length &&
          action.payload.subscriptions[0].turnCredentials))
    )
  }

  yield takeEvery(hasTurnCredentials, supportSagas.setTurnCredentials)
}

/**
 * Forward an incoming call.
 * @method forwardCallEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * forwardCallEntry (deps) {
  yield takeEvery(actionTypes.FORWARD_CALL, forwardCall, { ...deps, requests })
}

/**
 * Transfers 2 ongoing calls to one another.
 * @method consultativeTransferEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * consultativeTransferEntry (deps) {
  yield takeEvery(actionTypes.CONSULTATIVE_TRANSFER, midcallSagas.consultativeTransfer, { ...deps, requests })
}

/**
 * Direct Transfer an ongoing call.
 * @method directTransferEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * directTransferEntry (deps) {
  yield takeEvery(actionTypes.DIRECT_TRANSFER, midcallSagas.directTransfer, { ...deps, requests })
}

/**
 * Joins 2 ongoing calls.
 * @method joinEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * joinEntry (deps) {
  yield takeEvery(actionTypes.JOIN, midcallSagas.join, { ...deps, requests })
}

/**
 * Replaces a track with a new track with specified constraints.
 * @method replaceTrackEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * replaceTrackEntry (deps) {
  yield takeEvery(actionTypes.REPLACE_TRACK, midcallSagas.replaceTrack, deps)
}

/**
 * Retrieves the list of available codecs supported by the browser.
 * @method getAvailableCodecsEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * getAvailableCodecsEntry (deps) {
  yield takeEvery(actionTypes.GET_AVAILABLE_CODECS, getAvailableCodecs, deps)
}

/**
 * Restarts ICE candidate collection
 * @method restartIce
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * restartIce (deps) {
  yield takeEvery(actionTypes.MEDIA_RESTART, midcallSagas.iceRestart, { ...deps, requests })
}

/**
 * Behaviour: Normalize ICE connection state changes between Plan-B
 *    and Unified-Plan PeerConnections.
 * See saga description of `normalizeIceFailure` for full context.
 * @param  {Object} deps
 */
export function * watchForMediaDisconnect (deps) {
  // Watch for Session actions indicating the ICE connection has disconnected.
  function onMediaDisconnect (action) {
    return (
      action.type === webrtcActionTypes.SESSION_ICE_CONNECTION_STATE_CHANGE &&
      action.payload.iceConnectionState === CALL_MEDIA_CONNECTION_STATES.DISCONNECTED
    )
  }

  // We only need this behaviour when using Unified-Plan.
  const options = yield select(getOptions)
  if (options.defaultPeerConfig.sdpSemantics === 'unified-plan') {
    yield takeEvery(onMediaDisconnect, normalizeIceFailure)
  }
}

/**
 * Entry saga for events that trigger a call to the ice collection check function.
 * @method callCollectionCheckEntry
 * @param {Object} deps             Dependencies to be injected.
 * @param {Object} deps.webRTC      The WebRTC stack.
 */
export function * callCollectionCheckEntry (deps) {
  function onIceCollectionActions (action) {
    return (
      action.type === webrtcActionTypes.SESSION_ICE_GATHERING_STATE_CHANGE ||
      action.type === webrtcActionTypes.SESSION_ICE_CANDIDATE_COLLECTED ||
      action.type === webrtcActionTypes.SESSION_ICE_COLLECTION_SCHEDULED_CHECK
    )
  }

  yield takeEvery(onIceCollectionActions, callIceCollectionCheck, deps)
}
